import { defineStore } from 'pinia'
import { createStoreBase } from '@/presentation/stores/resources/storeBase.ts'
import { computed, type Ref, ref } from 'vue'
import type ExportFilterSchool from '@/core/domain/exports/ExportFilterSchool.ts'
import DependencyContainer from '@/presentation/configuration/DependencyContainer.ts'
import FetchAllExportFiltersUseCase from '@/core/useCases/exports/FetchAllExportFiltersUseCase.ts'
import ExportResult from '@/core/domain/exports/ExportResult.ts'
import ExportFilterInput from '@/core/domain/exports/ExportFilterInput.ts'
import FetchExportUseCase from '@/core/useCases/exports/FetchExportUseCase.ts'
import { ExportType } from '@/core/domain/resources/enums/ExportType.ts'
import type SchoolYear from '@/core/domain/school/year/SchoolYear.ts'
import { QuizType } from '@/core/domain/resources/enums/QuizType.ts'

interface ExportingState {
  type: ExportType.Values | null
  diagType: QuizType.Values | null
}

export const useExportsStore = defineStore('Exports', () => {
  // Utilisation de la base commune pour les stores
  const storeBase = createStoreBase(['fetchAllExportFilters', 'fetchExport'])

  const exportingState: Ref<ExportingState> = ref({ type: null, diagType: null })
  const sourceFilters: Ref<ExportFilterSchool[]> = ref<ExportFilterSchool[]>([])
  const exportSelectedSchoolYear: Ref<SchoolYear | null> = ref(null)

  /**
   * Action to fetch all export filters for a given school year.
   * @param schoolYearId
   */
  async function fetchAllExportFilters(schoolYearId: number): Promise<ExportFilterSchool[]> {
    try {
      storeBase.loading.value.fetchAllExportFilters = true
      storeBase.resetError()

      const result = await DependencyContainer.resolve(FetchAllExportFiltersUseCase).execute(
        schoolYearId
      )

      sourceFilters.value = result
      storeBase.updateLastUpdated()

      return result
    } catch (error) {
      storeBase.setError(error)
      return Promise.reject(error as Error)
    } finally {
      storeBase.loading.value.fetchAllExportFilters = false
    }
  }

  /**
   * Action to fetch an export for a given key and filters.
   * @param key
   * @param filters
   */
  async function fetchExport(
    key: ExportType.Values,
    filters: ExportFilterInput
  ): Promise<ExportResult> {
    try {
      storeBase.loading.value.fetchExport = true
      storeBase.resetError()

      return await DependencyContainer.resolve(FetchExportUseCase).execute(key, filters)
    } catch (error) {
      storeBase.showErrorToast(
        'Téléchargement impossible',
        "Une erreur est survenue lors de l'export, veuillez-vous rapprochez de l'équipe technique.",
        7500
      )
      storeBase.setError(error)
      return Promise.reject(error as Error)
    } finally {
      storeBase.loading.value.fetchExport = false
    }
  }

  /**
   * Set the selected school year.
   * @param schoolYear
   */
  function setExportSelectedSchoolYear(schoolYear: SchoolYear | null): void {
    exportSelectedSchoolYear.value = schoolYear
  }

  /**
   * Check if the exporting state exists.
   * @returns
   */
  const existsReportingState: Ref<boolean> = computed(() => exportingState.value.type !== null)

  /**
   * Set the exporting state.
   * @param type
   * @param diagType
   */
  function setExportingState(
    type: ExportType.Values | null,
    diagType: QuizType.Values | null = null
  ): void {
    exportingState.value = { type, diagType }
  }

  /**
   * Check if the exporting state is equal to the given type and diagType.
   * @param type
   * @param diagType
   */
  function equalsExportingState(
    type: ExportType.Values,
    diagType: QuizType.Values | null
  ): boolean {
    return exportingState.value.type === type && exportingState.value.diagType === diagType
  }

  /**
   * Réinitialise le store
   */
  function reset(): void {
    sourceFilters.value = []
    exportSelectedSchoolYear.value = null
    storeBase.resetBase()
  }

  return {
    exportingState,
    sourceFilters,
    exportSelectedSchoolYear,
    exportsStoreError: storeBase.error,
    exportsStoreLoading: storeBase.loading,
    ...storeBase,
    existsReportingState,
    setExportSelectedSchoolYear,
    fetchAllExportFilters,
    setExportingState,
    equalsExportingState,
    fetchExport,
    reset
  }
})